import { WORD_WHEEL_GRID_SIZE } from "./wordWheelGridConstants";

const EMPTY = "";
const DEFAULT_ATTEMPTS = 90;

function newCoordId() {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
        return crypto.randomUUID();
    }
    return `ww-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function shuffle(list, rng) {
    const next = [...list];
    for (let i = next.length - 1; i > 0; i -= 1) {
        const j = Math.floor(rng() * (i + 1));
        [next[i], next[j]] = [next[j], next[i]];
    }
    return next;
}

function mulberry32(seed) {
    let t = seed >>> 0;
    return () => {
        t += 0x6D2B79F5;
        let r = Math.imul(t ^ (t >>> 15), 1 | t);
        r ^= r + Math.imul(r ^ (r >>> 7), 61 | r);
        return ((r ^ (r >>> 14)) >>> 0) / 4294967296;
    };
}

function emptyGrid(size) {
    return Array.from({ length: size }, () => Array.from({ length: size }, () => EMPTY));
}

function cloneGrid(grid) {
    return grid.map((row) => [...row]);
}

export function wordsFromFilledCoordinates(filledCoordinates) {
    if (!Array.isArray(filledCoordinates)) {
        return [];
    }
    const seen = new Set();
    const words = [];
    for (const coord of filledCoordinates) {
        const raw = String(coord?.text || coord?.word || "").trim();
        if (!raw || coord?.direction === "scattered") {
            continue;
        }
        const key = raw.toLowerCase();
        if (seen.has(key)) {
            continue;
        }
        seen.add(key);
        words.push(raw.toLowerCase());
    }
    return words;
}

export function layoutFingerprint(coords) {
    if (!Array.isArray(coords) || coords.length === 0) {
        return "";
    }
    const parts = coords.map((coord) => {
        const word = String(coord?.text || coord?.word || "").toLowerCase();
        const direction = String(coord?.direction || "");
        const cells = (coord?.positions || [])
            .map((pos) => `${pos.row},${pos.col}`)
            .join(",");
        return `${word}|${direction}|${cells}`;
    });
    parts.sort();
    return parts.join(";");
}

function rebuildGridFromCoords(coords, size) {
    const grid = emptyGrid(size);
    for (const coord of coords || []) {
        for (const pos of coord.positions || []) {
            if (pos.row < 0 || pos.row >= size || pos.col < 0 || pos.col >= size) {
                continue;
            }
            grid[pos.row][pos.col] = pos.value || "";
        }
    }
    return grid;
}

function shiftCoords(coords, dRow, dCol) {
    if (!dRow && !dCol) {
        return coords;
    }
    return (coords || []).map((coord) => ({
        ...coord,
        positions: (coord.positions || []).map((pos) => ({
            ...pos,
            row: pos.row + dRow,
            col: pos.col + dCol,
        })),
    }));
}

/** Slide a finished crossword so its bounding box sits in the middle of the board. */
function centerLayout(result, gridSize) {
    if (!result?.coords?.length || !result.grid) {
        return result;
    }
    const bounds = occupiedBounds(result.grid);
    if (bounds.count === 0) {
        return result;
    }
    const dRow = Math.floor((gridSize - bounds.height) / 2) - bounds.minRow;
    const dCol = Math.floor((gridSize - bounds.width) / 2) - bounds.minCol;
    if (dRow === 0 && dCol === 0) {
        return result;
    }
    const coords = shiftCoords(result.coords, dRow, dCol);
    return { coords, grid: rebuildGridFromCoords(coords, gridSize) };
}

function occupiedBounds(grid) {
    const size = grid.length;
    let minRow = size;
    let minCol = size;
    let maxRow = -1;
    let maxCol = -1;
    let count = 0;
    for (let row = 0; row < size; row += 1) {
        for (let col = 0; col < size; col += 1) {
            if (!grid[row][col]) {
                continue;
            }
            count += 1;
            minRow = Math.min(minRow, row);
            minCol = Math.min(minCol, col);
            maxRow = Math.max(maxRow, row);
            maxCol = Math.max(maxCol, col);
        }
    }
    if (count === 0) {
        return { minRow: 0, minCol: 0, maxRow: 0, maxCol: 0, count: 0, width: 0, height: 0, area: 0 };
    }
    const width = maxCol - minCol + 1;
    const height = maxRow - minRow + 1;
    return { minRow, minCol, maxRow, maxCol, count, width, height, area: width * height };
}

function illegalAdjacencyCount(placed) {
    const cover = new Map();
    for (const coord of placed) {
        const horizontal = coord.direction === "horizontal";
        const id = coord.text;
        for (const pos of coord.positions || []) {
            const key = `${pos.row},${pos.col}`;
            if (!cover.has(key)) {
                cover.set(key, []);
            }
            cover.get(key).push({ horizontal, id });
        }
    }
    let bad = 0;
    for (const [key, owners] of cover.entries()) {
        const [row, col] = key.split(",").map(Number);
        const right = cover.get(`${row},${col + 1}`);
        if (right) {
            const shareHorizontal = owners.some((owner) => owner.horizontal
                && right.some((other) => other.horizontal && other.id === owner.id));
            if (!shareHorizontal) {
                bad += 1;
            }
        }
        const below = cover.get(`${row + 1},${col}`);
        if (below) {
            const shareVertical = owners.some((owner) => !owner.horizontal
                && below.some((other) => !other.horizontal && other.id === owner.id));
            if (!shareVertical) {
                bad += 1;
            }
        }
    }
    return bad;
}

function scoreLayout(grid, placed) {
    const bounds = occupiedBounds(grid);
    if (bounds.count === 0) {
        return 0;
    }
    const compactness = bounds.count / Math.max(1, bounds.area);
    const center = (grid.length - 1) / 2;
    const midRow = (bounds.minRow + bounds.maxRow) / 2;
    const midCol = (bounds.minCol + bounds.maxCol) / 2;
    const offCenter = Math.abs(midRow - center) + Math.abs(midCol - center);
    let centroidSpread = 0;
    const centroids = placed.map((coord) => {
        const positions = coord.positions || [];
        const sum = positions.reduce(
            (acc, pos) => ({ row: acc.row + pos.row, col: acc.col + pos.col }),
            { row: 0, col: 0 }
        );
        return {
            row: sum.row / Math.max(1, positions.length),
            col: sum.col / Math.max(1, positions.length),
        };
    });
    for (let i = 0; i < centroids.length; i += 1) {
        for (let j = i + 1; j < centroids.length; j += 1) {
            centroidSpread += Math.abs(centroids[i].row - centroids[j].row)
                + Math.abs(centroids[i].col - centroids[j].col);
        }
    }
    const illegal = illegalAdjacencyCount(placed);
    return (bounds.area * 12)
        + (bounds.width + bounds.height) * 4
        + centroidSpread * 3
        - compactness * 40
        - offCenter * 30
        - illegal * 220;
}

function canPlaceWord(grid, word, startRow, startCol, horizontal, { strictCrossword }) {
    const size = grid.length;
    const len = word.length;
    if (horizontal) {
        if (startRow < 0 || startRow >= size || startCol < 0 || startCol + len > size) {
            return false;
        }
        if (startCol > 0 && grid[startRow][startCol - 1]) {
            return false;
        }
        if (startCol + len < size && grid[startRow][startCol + len]) {
            return false;
        }
    } else if (startCol < 0 || startCol >= size || startRow < 0 || startRow + len > size) {
        return false;
    }
    if (!horizontal) {
        if (startRow > 0 && grid[startRow - 1][startCol]) {
            return false;
        }
        if (startRow + len < size && grid[startRow + len][startCol]) {
            return false;
        }
    }

    let crossings = 0;
    let consecutiveFilled = 0;
    for (let i = 0; i < len; i += 1) {
        const row = horizontal ? startRow : startRow + i;
        const col = horizontal ? startCol + i : startCol;
        const existing = grid[row][col];
        const letter = word[i];
        if (existing) {
            if (existing !== letter) {
                return false;
            }
            crossings += 1;
            consecutiveFilled += 1;
            if (consecutiveFilled > 1) {
                return false;
            }
        } else {
            consecutiveFilled = 0;
            if (strictCrossword) {
                if (horizontal) {
                    if (row > 0 && grid[row - 1][col]) {
                        return false;
                    }
                    if (row + 1 < size && grid[row + 1][col]) {
                        return false;
                    }
                } else {
                    if (col > 0 && grid[row][col - 1]) {
                        return false;
                    }
                    if (col + 1 < size && grid[row][col + 1]) {
                        return false;
                    }
                }
            }
        }
    }
    return crossings >= 1;
}

function firstSlotCenterDistance(slot, len, center) {
    const midRow = slot.horizontal ? slot.row : slot.row + (len - 1) / 2;
    const midCol = slot.horizontal ? slot.col + (len - 1) / 2 : slot.col;
    return Math.abs(midRow - center) + Math.abs(midCol - center);
}

function firstWordSlots(gridSize, word, rng) {
    const len = word.length;
    const center = (gridSize - 1) / 2;
    const slots = [];
    for (const horizontal of [true, false]) {
        if (horizontal) {
            for (let row = 0; row < gridSize; row += 1) {
                for (let col = 0; col <= gridSize - len; col += 1) {
                    slots.push({ row, col, horizontal });
                }
            }
        } else {
            for (let col = 0; col < gridSize; col += 1) {
                for (let row = 0; row <= gridSize - len; row += 1) {
                    slots.push({ row, col, horizontal });
                }
            }
        }
    }
    slots.sort((a, b) => {
        const distA = firstSlotCenterDistance(a, len, center);
        const distB = firstSlotCenterDistance(b, len, center);
        if (distA !== distB) {
            return distA - distB;
        }
        return rng() < 0.5 ? -1 : 1;
    });
    return slots;
}

function collectAnchorCells(grid) {
    const size = grid.length;
    const cells = [];
    for (let row = 0; row < size; row += 1) {
        for (let col = 0; col < size; col += 1) {
            if (grid[row][col]) {
                cells.push({ row, col, letter: grid[row][col] });
            }
        }
    }
    return cells;
}

function placementsForWord(grid, word, { strictCrossword }) {
    const placements = [];
    const anchors = collectAnchorCells(grid);
    for (const anchor of anchors) {
        for (let idx = 0; idx < word.length; idx += 1) {
            if (word[idx] !== anchor.letter) {
                continue;
            }
            const startRowH = anchor.row;
            const startColH = anchor.col - idx;
            if (canPlaceWord(grid, word, startRowH, startColH, true, { strictCrossword })) {
                placements.push({ row: startRowH, col: startColH, horizontal: true, crossIndex: idx });
            }
            const startRowV = anchor.row - idx;
            const startColV = anchor.col;
            if (canPlaceWord(grid, word, startRowV, startColV, false, { strictCrossword })) {
                placements.push({ row: startRowV, col: startColV, horizontal: false, crossIndex: idx });
            }
        }
    }
    return placements;
}

function applyWord(grid, word, row, col, horizontal) {
    const nextGrid = cloneGrid(grid);
    const positions = [];
    for (let i = 0; i < word.length; i += 1) {
        const r = horizontal ? row : row + i;
        const c = horizontal ? col + i : col;
        nextGrid[r][c] = word[i];
        positions.push({ row: r, col: c, value: word[i] });
    }
    return {
        grid: nextGrid,
        coord: {
            id: newCoordId(),
            word,
            text: word,
            length: word.length,
            direction: horizontal ? "horizontal" : "vertical",
            reversed: false,
            positions,
        },
    };
}

function placementScore(grid, word, placement) {
    const applied = applyWord(grid, word, placement.row, placement.col, placement.horizontal);
    const bounds = occupiedBounds(applied.grid);
    const endCross = placement.crossIndex === 0 || placement.crossIndex === word.length - 1;
    return bounds.area * 8
        + (bounds.width + bounds.height) * 3
        + (endCross ? 12 : 0);
}

function pickPlacement(placements, grid, word, rng) {
    if (placements.length === 0) {
        return null;
    }
    const ranked = placements
        .map((placement) => ({ placement, score: placementScore(grid, word, placement) }))
        .sort((a, b) => b.score - a.score);
    const topCount = Math.min(4, ranked.length);
    const top = ranked.slice(0, topCount);
    if (rng() < 0.75) {
        return top[Math.floor(rng() * top.length)].placement;
    }
    return ranked[Math.floor(rng() * ranked.length)].placement;
}

function tryLayoutFromFirst(ordered, firstSlot, gridSize, rng, strictCrossword) {
    const first = ordered[0];
    let { grid, coord } = applyWord(emptyGrid(gridSize), first, firstSlot.row, firstSlot.col, firstSlot.horizontal);
    const placed = [coord];
    const remaining = ordered.slice(1);

    while (remaining.length > 0) {
        let placedAny = false;
        const tryOrder = shuffle(remaining, rng);
        for (const word of tryOrder) {
            const options = placementsForWord(grid, word, { strictCrossword });
            const chosen = pickPlacement(options, grid, word, rng);
            if (!chosen) {
                continue;
            }
            const applied = applyWord(grid, word, chosen.row, chosen.col, chosen.horizontal);
            grid = applied.grid;
            placed.push(applied.coord);
            remaining.splice(remaining.indexOf(word), 1);
            placedAny = true;
            break;
        }
        if (!placedAny) {
            return null;
        }
    }
    return { coords: placed, grid };
}

function orderedWordsForAttempt(words, rng) {
    if (rng() < 0.4) {
        return [...words].sort((a, b) => b.length - a.length || a.localeCompare(b));
    }
    return shuffle(words, rng);
}

function tryLayout(words, gridSize, rng, strictCrossword) {
    const ordered = orderedWordsForAttempt(words, rng);
    const firstSlots = firstWordSlots(gridSize, ordered[0], rng).slice(0, 4);
    for (const firstSlot of firstSlots) {
        const result = tryLayoutFromFirst(ordered, firstSlot, gridSize, rng, strictCrossword);
        if (result && illegalAdjacencyCount(result.coords) === 0) {
            return result;
        }
        if (result && !strictCrossword) {
            return result;
        }
    }
    return null;
}

function rankedPlacements(grid, word, strictCrossword) {
    return placementsForWord(grid, word, { strictCrossword })
        .map((placement) => ({ placement, score: placementScore(grid, word, placement) }))
        .sort((a, b) => b.score - a.score)
        .map((entry) => entry.placement);
}

function backtrackLayout(words, gridSize, rng, strictCrossword) {
    const ordered = orderedWordsForAttempt(words, rng);
    const firstSlots = firstWordSlots(gridSize, ordered[0], rng).slice(0, 12);
            const budget = { left: 4000 };

    const search = (grid, placed, remaining) => {
        if (budget.left <= 0) {
            return null;
        }
        budget.left -= 1;
        if (remaining.length === 0) {
            if (strictCrossword && illegalAdjacencyCount(placed) > 0) {
                return null;
            }
            return { coords: placed, grid };
        }
        const rankedWords = remaining
            .map((word) => ({ word, count: placementsForWord(grid, word, { strictCrossword }).length }))
            .sort((a, b) => a.count - b.count);
        if (rankedWords[0].count === 0) {
            return null;
        }
        const word = rankedWords[0].word;
        const options = rankedPlacements(grid, word, strictCrossword).slice(0, 10);
        for (const chosen of options) {
            const applied = applyWord(grid, word, chosen.row, chosen.col, chosen.horizontal);
            const found = search(
                applied.grid,
                [...placed, applied.coord],
                remaining.filter((item) => item !== word)
            );
            if (found) {
                return found;
            }
        }
        return null;
    };

    for (const firstSlot of firstSlots) {
        const applied = applyWord(
            emptyGrid(gridSize),
            ordered[0],
            firstSlot.row,
            firstSlot.col,
            firstSlot.horizontal
        );
        const found = search(applied.grid, [applied.coord], ordered.slice(1));
        if (found) {
            return found;
        }
    }
    return null;
}

/**
 * Rebuild a connected crossword on the Word Wheel grid, preferring spread-out
 * placements (words stay crossing, but are less clumped than the chain layout).
 */
export function scatterWordWheelLayout(words, options = {}) {
    const gridSize = options.gridSize || WORD_WHEEL_GRID_SIZE;
    const attempts = options.attempts || DEFAULT_ATTEMPTS;
    const avoidFingerprint = options.avoidFingerprint || "";
    const seed = options.seed ?? Date.now();
    const normalized = (words || [])
        .map((word) => String(word || "").trim().toLowerCase())
        .filter((word) => word.length >= 3 && word.length <= gridSize);

    if (normalized.length < 3) {
        return { ok: false, reason: "Need at least 3 words to rearrange." };
    }

    const rng = mulberry32(seed >>> 0);
    let best = null;
    let bestScore = Number.NEGATIVE_INFINITY;
    let fallback = null;
    let fallbackScore = Number.NEGATIVE_INFINITY;

    const consider = (result) => {
        if (!result || result.coords.length !== normalized.length) {
            return;
        }
        result = centerLayout(result, gridSize);
        const fingerprint = layoutFingerprint(result.coords);
        const score = scoreLayout(result.grid, result.coords);
        if (!fallback || score > fallbackScore) {
            fallback = { ...result, fingerprint };
            fallbackScore = score;
        }
        if (avoidFingerprint && fingerprint === avoidFingerprint) {
            return;
        }
        if (score > bestScore) {
            best = { ...result, fingerprint };
            bestScore = score;
        }
    };

    for (let i = 0; i < attempts; i += 1) {
        consider(tryLayout(normalized, gridSize, rng, true));
    }
    for (let i = 0; i < 10; i += 1) {
        consider(backtrackLayout(normalized, gridSize, rng, true));
    }
    if (!best || illegalAdjacencyCount(best.coords) > 0) {
        for (let i = 0; i < Math.ceil(attempts / 3); i += 1) {
            consider(tryLayout(normalized, gridSize, rng, false));
        }
    }

    const chosen = best || fallback;
    if (!chosen) {
        return { ok: false, reason: "Could not find a connected crossword layout for these words." };
    }
    if (avoidFingerprint && chosen.fingerprint === avoidFingerprint && !best) {
        return { ok: false, reason: "Could not find a more spread-out layout. Try again." };
    }
    return { ok: true, coords: chosen.coords, fingerprint: chosen.fingerprint };
}

function normalizeWordList(words) {
    if (Array.isArray(words)) {
        return words.map((word) => String(word || "").trim().toLowerCase()).filter(Boolean);
    }
    return String(words || "")
        .split(/[\n,;]+/)
        .map((word) => word.trim().toLowerCase())
        .filter(Boolean);
}

function parseCoordsInput(filledCoordinates) {
    if (!filledCoordinates) {
        return [];
    }
    if (Array.isArray(filledCoordinates)) {
        return filledCoordinates;
    }
    try {
        const parsed = JSON.parse(filledCoordinates);
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
}

function positionLetter(pos) {
    return String(pos?.value ?? pos?.letter ?? pos?.char ?? "").trim().toLowerCase();
}

/** Word letters must sit on a contiguous horizontal/vertical run (order or reverse). */
function wordCellsCovered(word, positions, gridSize) {
    if (!word || !Array.isArray(positions) || positions.length !== word.length) {
        return null;
    }
    const cells = [];
    for (const pos of positions) {
        const row = Number(pos?.row);
        const col = Number(pos?.col);
        const letter = positionLetter(pos);
        if (!Number.isInteger(row) || !Number.isInteger(col)
            || row < 0 || row >= gridSize || col < 0 || col >= gridSize
            || !letter) {
            return null;
        }
        cells.push({ row, col, letter });
    }

    const rows = new Set(cells.map((cell) => cell.row));
    const cols = new Set(cells.map((cell) => cell.col));
    let ordered;
    if (rows.size === 1 && cols.size === word.length) {
        ordered = [...cells].sort((a, b) => a.col - b.col);
        for (let i = 1; i < ordered.length; i += 1) {
            if (ordered[i].col !== ordered[i - 1].col + 1) {
                return null;
            }
        }
    } else if (cols.size === 1 && rows.size === word.length) {
        ordered = [...cells].sort((a, b) => a.row - b.row);
        for (let i = 1; i < ordered.length; i += 1) {
            if (ordered[i].row !== ordered[i - 1].row + 1) {
                return null;
            }
        }
    } else {
        return null;
    }

    const forward = ordered.map((cell) => cell.letter).join("");
    const reverse = [...ordered].reverse().map((cell) => cell.letter).join("");
    if (forward !== word && reverse !== word) {
        return null;
    }
    return ordered.map((cell) => `${cell.row},${cell.col}`);
}

/**
 * True when every expected word is placed on the grid with matching letters,
 * and the placements form one connected crossword (each multi-word layout has
 * shared cross-letters — no isolated words).
 */
export function isValidCrosswordLayout(filledCoordinates, wordsInUse, gridSize = WORD_WHEEL_GRID_SIZE) {
    const expected = normalizeWordList(wordsInUse);
    if (expected.length === 0) {
        return false;
    }

    const coords = parseCoordsInput(filledCoordinates);
    const placements = [];
    const seen = new Set();

    for (const coord of coords) {
        if (coord?.direction === "scattered") {
            continue;
        }
        const word = String(coord?.text || coord?.word || "").trim().toLowerCase();
        if (!word || seen.has(word)) {
            continue;
        }
        const cells = wordCellsCovered(word, coord?.positions, gridSize);
        if (!cells) {
            return false;
        }
        seen.add(word);
        placements.push({ word, cells });
    }

    for (const word of expected) {
        if (!seen.has(word)) {
            return false;
        }
    }

    const expectedSet = new Set(expected);
    const relevant = placements.filter((item) => expectedSet.has(item.word));
    if (relevant.length !== expected.length) {
        return false;
    }
    if (relevant.length === 1) {
        return true;
    }

    const cellOwners = new Map();
    relevant.forEach((item, index) => {
        for (const cell of item.cells) {
            if (!cellOwners.has(cell)) {
                cellOwners.set(cell, []);
            }
            cellOwners.get(cell).push(index);
        }
    });

    const adjacency = relevant.map(() => new Set());
    for (const owners of cellOwners.values()) {
        if (owners.length < 2) {
            continue;
        }
        for (let i = 0; i < owners.length; i += 1) {
            for (let j = i + 1; j < owners.length; j += 1) {
                adjacency[owners[i]].add(owners[j]);
                adjacency[owners[j]].add(owners[i]);
            }
        }
    }

    if (adjacency.some((neighbors) => neighbors.size === 0)) {
        return false;
    }

    const visited = new Set([0]);
    const queue = [0];
    while (queue.length > 0) {
        const current = queue.shift();
        for (const next of adjacency[current]) {
            if (visited.has(next)) {
                continue;
            }
            visited.add(next);
            queue.push(next);
        }
    }
    return visited.size === relevant.length;
}
